import api from './axios';

export interface ExportType {
    id: string;
    name: string;
    description: string;
    formats: string[];
}

export interface ExportJob {
    job_id: string;
    type: string;
    status: 'queued' | 'running' | 'ready' | 'failed';
    created_at: string;
    completed_at?: string;
    file_size?: number;
    row_count?: number;
    error?: string;
    format?: string;
    source?: 'db' | 'live';
}

export interface GenerateExportRequest {
    type: string;
    business_id: string;
    start_date?: string;
    end_date?: string;
    format?: string;
    source?: 'db' | 'live';
}

export interface AdminQboSyncRequest {
    business_id: string;
    type: string;
    start_date?: string;
    end_date?: string;
}

export interface QbDesktopConnectorTokenResponse {
    business_id: string;
    connector_id: string;
    token: string;
    created_at: string;
}

export type AdminLogSource = 'all' | 'llm_audit' | 'chat_feedback' | 'cache_job' | 'api_audit' | 'system_log';

export interface AdminLogItem {
    id: string;
    source: Exclude<AdminLogSource, 'all'>;
    timestamp: string | null;
    business_id?: string | null;
    status: 'ok' | 'error';
    summary: string;
    details: Record<string, unknown>;
}

export interface AdminLogsResponse {
    window_days: number;
    source: AdminLogSource;
    business_id?: string | null;
    errors_only: boolean;
    search?: string | null;
    page: number;
    limit: number;
    total: number;
    has_next: boolean;
    count: number;
    counts_by_source: {
        llm_audit: number;
        chat_feedback: number;
        cache_job: number;
        api_audit: number;
        system_log: number;
    };
    items: AdminLogItem[];
}

export interface AdminLogFilterOption {
    value: string;
    label: string;
}

export interface AdminAuditLogEntry {
    _id: string;
    event_id: string;
    timestamp: string;
    user_id: string;
    event_type: string;
    description: string;
    business_id?: string;
    workspace_id?: string;
    request_id?: string;
    metadata: Record<string, any>;
    before?: any;
    after?: any;
}

export interface SearchableAuditOption {
    id: string;
    name: string;
    subtitle?: string;
}

export interface AdminAuditLogsResponse {
    items: AdminAuditLogEntry[];
    count: number;
    total: number;
    page: number;
    limit: number;
    has_next: boolean;
}

export interface AdminLogFiltersResponse {
    sources: AdminLogFilterOption[];
    statuses: AdminLogFilterOption[];
    action_prefixes: string[];
}

export interface AdminLogsRequest {
    business_id?: string;
    source?: AdminLogSource;
    days?: number;
    limit?: number;
    page?: number;
    errors_only?: boolean;
    status?: 'ok' | 'fail' | string;
    action_prefix?: string;
    search?: string;
}

export type DebugFeedbackFilter = 'all' | 'good' | 'bad' | 'none';
export type DebugVisibilityMode = 'masked' | 'raw';

export interface DebugConversationRow {
    conversation_id: string;
    business_id?: string | null;
    title?: string | null;
    last_trace_at?: string | null;
    last_updated?: string | null;
    latest_route_type?: string | null;
    latest_template_id?: string | null;
    latest_cache_metric?: string | null;
    latest_status?: string | null;
    latest_question_preview?: string;
    latest_answer_id?: string | null;
    answer_count: number;
    bad_feedback_count: number;
    message_count?: number;
    trace_completeness?: 'full' | 'partial' | string;
}

export interface DebugConversationsResponse {
    business_id?: string | null;
    route_type?: string | null;
    template_id?: string | null;
    status?: string | null;
    days: number;
    has_bad_feedback: boolean;
    search?: string | null;
    limit: number;
    cursor?: string | null;
    next_cursor?: string | null;
    count: number;
    items: DebugConversationRow[];
}

export interface DebugAnswerRow {
    trace_id: string;
    answer_id?: string | null;
    conversation_id?: string | null;
    answer_message_id?: string | null;
    parent_user_message_id?: string | null;
    business_id?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
    status?: string | null;
    question_preview?: string;
    answer_preview?: string;
    route_type?: string | null;
    complexity?: string | null;
    template_id?: string | null;
    cache_metric?: string | null;
    routing_strategy?: string | null;
    route_confidence?: number | null;
    decision_notes?: string[];
    response_variant?: string | null;
    latest_feedback?: Record<string, unknown> | null;
    evaluation?: Record<string, unknown> | null;
    request_id?: string | null;
    primary_invocation_id?: string | null;
    data_readiness_status?: string | null;
}

export interface DebugAnswersResponse {
    business_id?: string | null;
    days: number;
    limit: number;
    cursor?: string | null;
    next_cursor?: string | null;
    count: number;
    items: DebugAnswerRow[];
}

export interface DebugConversationDetail {
    conversation_id: string;
    business_id?: string | null;
    workspace_id?: string | null;
    title?: string | null;
    created_at?: string | null;
    last_updated?: string | null;
    message_count: number;
    trace_count: number;
    turns: Array<Record<string, unknown>>;
    trace_summaries: Array<Record<string, unknown>>;
}

export interface DebugAnswerDetail extends Record<string, unknown> {
    trace_id?: string;
    answer_id?: string;
    routing?: Record<string, unknown>;
    timeline?: Array<Record<string, unknown>>;
    tool_results_summary?: Array<Record<string, unknown>>;
    tool_results_inline?: Array<Record<string, unknown>>;
    tool_results_full_ref?: Record<string, unknown> | null;
    data_readiness_snapshot?: Record<string, unknown>;
    runtime?: Record<string, unknown>;
    revisions_snapshot?: Array<Record<string, unknown>>;
    feedback_history?: Array<Record<string, unknown>>;
    llm_invocations?: Array<Record<string, unknown>>;
}

export interface DebugArtifactResponse {
    answer_id: string;
    trace_id: string;
    artifact_id: string;
    artifact_type: string;
    chunk_count: number;
    payload: unknown;
}

export interface DebugEvaluationRequest {
    severity?: 'low' | 'medium' | 'high';
    correctness_label?: 'correct' | 'partially_correct' | 'incorrect' | 'not_applicable';
    error_tags?: string[];
    expected_answer_note?: string;
    evaluator_note?: string;
}

export interface DebugAnswersRequest {
    business_id?: string;
    route_type?: string;
    template_id?: string;
    feedback?: DebugFeedbackFilter;
    reason_code?: string;
    status?: string;
    days?: number;
    search?: string;
    limit?: number;
    cursor?: string;
}

export interface DebugConversationsRequest {
    business_id?: string;
    route_type?: string;
    template_id?: string;
    status?: string;
    days?: number;
    has_bad_feedback?: boolean;
    search?: string;
    limit?: number;
    cursor?: string;
}

export interface DebugExportRequest {
    scope: 'answers' | 'conversations' | 'feedback';
    format: 'csv' | 'json';
    business_id?: string;
    route_type?: string;
    template_id?: string;
    feedback?: DebugFeedbackFilter;
    reason_code?: string;
    status?: string;
    days?: number;
    search?: string;
    limit?: number;
}

export interface AdminOpsJob {
    job_id: string;
    job_kind: 'export' | 'etl' | string;
    job_type: string;
    status: string;
    started_at?: string | null;
    finished_at?: string | null;
    created_at?: string | null;
    error?: string | null;
    count_processed?: number | null;
    format?: string | null;
    source?: string | null;
}

export interface AdminOpsBusinessStatus {
    business_id: string;
    business_name: string;
    owner_email?: string | null;
    connections: {
        qbo_linked: boolean;
        stripe_linked: boolean;
    };
    active_work_count: number;
    last_activity_at?: string | null;
    qbo_account_mapping: {
        status: string;
        detail?: string | null;
        stats?: Record<string, unknown>;
        started_at?: string | null;
        updated_at?: string | null;
        finished_at?: string | null;
    };
    qbo_connector?: {
        provider?: string | null;
        source?: string | null;
        status?: string | null;
        last_heartbeat_at?: string | null;
        last_successful_sync_at?: string | null;
        last_sync_status?: string | null;
        last_error?: string | null;
        queued_payload_count?: number | null;
        machine_name?: string | null;
        company_file?: string | null;
        connector_id?: string | null;
        connector_version?: string | null;
    };
    vendor_resolution: {
        pending_ai_count: number;
        human_verify_count: number;
        historical_only_count: number;
        needs_review_count: number;
        processing_count: number;
        deferred_history_count: number;
        total_flagged_count: number;
        is_complete: boolean;
    };
    jobs: {
        export_active_count: number;
        etl_active_count: number;
        export_recent: AdminOpsJob[];
        etl_recent: AdminOpsJob[];
    };
}

export interface AdminOpsStatusResponse {
    generated_at: string;
    filters: {
        business_id?: string | null;
        days: number;
        per_business_job_limit: number;
        include_idle: boolean;
    };
    summary: {
        businesses: number;
        qbo_mapping_running: number;
        etl_running: number;
        export_running: number;
        vendor_backlog_businesses: number;
        pending_vendor_transactions: number;
    };
    items: AdminOpsBusinessStatus[];
}

export interface AdminOpsStatusRequest {
    business_id?: string;
    days?: number;
    per_business_job_limit?: number;
    include_idle?: boolean;
}

export interface LlmProfile {
    provider?: 'gemini' | 'vertex' | 'openai';
    model?: string;
    temperature?: number;
    max_tokens?: number;
    thinking_tokens?: number;
    stream?: boolean;
}

export interface LlmConfigValues {
    default_max_tokens: number;
    default_temperature: number;
    qbo_mapping_max_tokens?: number;
    qbo_mapping_temperature?: number;
    invoice_extraction_max_tokens?: number;
    invoice_vision_max_tokens?: number;
    invoice_vision_model?: string;
    profiles: Record<string, LlmProfile>;
}

export interface LlmConfig {
    effective: LlmConfigValues;
    overrides: Partial<LlmConfigValues>;
    defaults: LlmConfigValues;
    last_updated?: string | null;
}

export interface InvoiceProcessingSettings {
    image_text_provider: 'ocr' | 'llm' | 'llm_text' | 'llm_json' | 'hybrid';
    vision_provider: 'gemini' | 'vertex' | 'openai';
    vision_model?: string | null;
    vision_max_tokens: number;
    updated_at?: string | null;
    updated_by?: string | null;
}

export interface WorkerRuntimeSettings {
    vertex_input_tpm_limit: number;
    gemini_input_tpm_limit: number;
    llm_multimodal_image_token_estimate: number;
    vendor_resolution_llm_request_delay_seconds: number;
    vendor_resolution_inter_batch_delay_seconds: number;
    vendor_resolution_llm_max_retries: number;
    vendor_resolution_llm_backoff_base_seconds: number;
    vendor_resolution_llm_backoff_max_seconds: number;
    invoice_llm_request_delay_seconds: number;
    invoice_llm_max_retries: number;
    invoice_llm_backoff_base_seconds: number;
    invoice_llm_backoff_max_seconds: number;
    updated_at?: string | null;
    updated_by?: string | null;
}

export const adminDataApi = {
    listExportTypes: async (): Promise<ExportType[]> => {
        try {
            const response = await api.get('/admin/data/exports/list');
            return response.data;
        } catch (error) {
            console.error('Failed to list export types:', error);
            throw error;
        }
    },

    listExportJobs: async (businessId: string): Promise<ExportJob[]> => {
        try {
            const response = await api.get('/admin/data/exports/jobs', {
                params: { business_id: businessId }
            });
            return response.data;
        } catch (error) {
            console.error('Failed to list export jobs:', error);
            throw error;
        }
    },

    generateExport: async (request: GenerateExportRequest): Promise<{ job_id: string, status: string }> => {
        try {
            const response = await api.post('/admin/data/exports/generate', request);
            return response.data;
        } catch (error) {
            console.error('Failed to generate export:', error);
            throw error;
        }
    },

    getDownloadUrl: async (jobId: string): Promise<{ url: string }> => {
        try {
            const response = await api.get(`/admin/data/exports/download/${jobId}`);
            return response.data;
        } catch (error) {
            console.error('Failed to get download URL:', error);
            throw error;
        }
    },

    syncQbo: async (request: AdminQboSyncRequest): Promise<{ status: string; message: string; type: string }> => {
        try {
            const response = await api.post('/admin/data/qbo/sync', request);
            return response.data;
        } catch (error) {
            console.error('Failed to sync QBO:', error);
            throw error;
        }
    },

    createQbDesktopConnectorToken: async (businessId: string): Promise<QbDesktopConnectorTokenResponse> => {
        const response = await api.post('/admin/data/qbdesktop/tokens', { business_id: businessId });
        return response.data;
    },

    getLogs: async (params: AdminLogsRequest = {}): Promise<AdminLogsResponse> => {
        try {
            const response = await api.get('/admin/data/logs', { params });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch admin logs:', error);
            throw error;
        }
    },

    getLogFilters: async (): Promise<AdminLogFiltersResponse> => {
        try {
            const response = await api.get('/admin/data/logs/filters');
            return response.data;
        } catch (error) {
            console.error('Failed to fetch admin log filters:', error);
            throw error;
        }
    },

    getSingleLog: async (source: string, logId: string): Promise<Record<string, unknown>> => {
        try {
            const response = await api.get(`/admin/data/logs/${source}/${logId}`);
            return response.data;
        } catch (error) {
            console.error(`Failed to fetch single admin log for ${source}/${logId}:`, error);
            throw error;
        }
    },

    getOpsStatus: async (params: AdminOpsStatusRequest = {}): Promise<AdminOpsStatusResponse> => {
        try {
            const response = await api.get('/admin/data/ops/status', { params });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch admin ops status:', error);
            throw error;
        }
    },

    getInvoiceProcessingSettings: async (): Promise<InvoiceProcessingSettings> => {
        const response = await api.get('/admin/data/invoice-processing/settings');
        return response.data;
    },

    updateInvoiceProcessingSettings: async (payload: InvoiceProcessingSettings): Promise<InvoiceProcessingSettings> => {
        const response = await api.put('/admin/data/invoice-processing/settings', payload);
        return response.data;
    },

    getWorkerRuntimeSettings: async (): Promise<WorkerRuntimeSettings> => {
        const response = await api.get('/admin/data/worker-runtime/settings');
        return response.data;
    },

    updateWorkerRuntimeSettings: async (payload: WorkerRuntimeSettings): Promise<WorkerRuntimeSettings> => {
        const response = await api.put('/admin/data/worker-runtime/settings', payload);
        return response.data;
    },

    getLlmConfig: async (): Promise<LlmConfig> => {
        const response = await api.get('/admin/config/llm');
        return response.data;
    },

    updateLlmConfig: async (payload: Partial<LlmConfigValues>): Promise<LlmConfig> => {
        const response = await api.patch('/admin/config/llm', payload);
        return response.data;
    },

    getDebugConversations: async (params: DebugConversationsRequest = {}): Promise<DebugConversationsResponse> => {
        const response = await api.get('/admin/data/debug/conversations', { params });
        return response.data;
    },

    getDebugConversation: async (
        conversationId: string,
        includeRaw = false,
        businessId?: string,
    ): Promise<DebugConversationDetail> => {
        const response = await api.get(`/admin/data/debug/conversations/${conversationId}`, {
            params: { include_raw: includeRaw, business_id: businessId },
        });
        return response.data;
    },

    getDebugAnswers: async (params: DebugAnswersRequest = {}): Promise<DebugAnswersResponse> => {
        const response = await api.get('/admin/data/debug/answers', { params });
        return response.data;
    },

    getDebugAnswerDetail: async (answerId: string, includeRaw = false): Promise<DebugAnswerDetail> => {
        if (!answerId || answerId === 'null' || answerId === 'undefined') {
            throw new Error('Invalid answer_id for debug detail lookup');
        }
        const response = await api.get(`/admin/data/debug/answers/${answerId}`, { params: { include_raw: includeRaw } });
        return response.data;
    },

    getDebugTraceDetail: async (traceId: string, includeRaw = false): Promise<DebugAnswerDetail> => {
        if (!traceId || traceId === 'null' || traceId === 'undefined') {
            throw new Error('Invalid trace_id for debug detail lookup');
        }
        const response = await api.get(`/admin/data/debug/traces/${traceId}`, { params: { include_raw: includeRaw } });
        return response.data;
    },

    getDebugArtifact: async (answerId: string, artifactId: string): Promise<DebugArtifactResponse> => {
        const response = await api.get(`/admin/data/debug/answers/${answerId}/artifacts/${artifactId}`);
        return response.data;
    },

    saveDebugEvaluation: async (answerId: string, payload: DebugEvaluationRequest): Promise<{ status: string }> => {
        const response = await api.post(`/admin/data/debug/answers/${answerId}/evaluation`, payload);
        return response.data;
    },

    exportDebug: async (params: DebugExportRequest): Promise<Blob | Record<string, unknown>> => {
        const response = await api.get('/admin/data/debug/exports', {
            params,
            responseType: params.format === 'csv' ? 'blob' : 'json',
        });
        return response.data;
    },

    getLoginAuditLogs: async (params: { user_id?: string; business_id?: string; workspace_id?: string; event_type?: string; status?: string; search?: string; days?: number; limit?: number; page?: number } = {}): Promise<AdminAuditLogsResponse> => {
        const response = await api.get('/admin/data/audit/logins', { params });
        return response.data;
    },

    getActionAuditLogs: async (params: { user_id?: string; business_id?: string; workspace_id?: string; event_type?: string; search?: string; days?: number; limit?: number; page?: number } = {}): Promise<AdminAuditLogsResponse> => {
        const response = await api.get('/admin/data/audit/actions', { params });
        return response.data;
    },

    getAuditFilters: async (params: { days?: number } = {}): Promise<{ users: SearchableAuditOption[], businesses: SearchableAuditOption[], workspaces: SearchableAuditOption[], action_event_types: SearchableAuditOption[] }> => {
        const response = await api.get('/admin/data/audit/filters', { params });
        return response.data;
    },

    getSystemAuditLogs: async (params: { user_id?: string; business_id?: string; workspace_id?: string; search?: string; days?: number; limit?: number; page?: number } = {}): Promise<AdminAuditLogsResponse> => {
        const response = await api.get('/admin/data/audit/system', { params });
        return response.data;
    },
};

export default adminDataApi;
